/**
 * CSS zoom compensation for the Pearl shell.
 *
 * Some host pages apply `zoom` on <body> (e.g. zoom: 1.79 on charting
 * views). CSS `zoom` compounds through ancestors and affects even Shadow DOM
 * content, so the shell applies an inverse zoom on its wrapper to stay at
 * the intended visual size.
 *
 * Hosts sometimes set zoom after initial page load; we retry detection
 * at 500 ms, 1s, 2s, 4s to catch late-set values.
 */

export const DEFAULT_ZOOM_RETRY_DELAYS: readonly number[] = [
  500, 1000, 2000, 4000,
];

interface ZoomWin {
  getComputedStyle: (el: unknown) => { zoom?: string };
  document: { documentElement: unknown; body: unknown };
}

interface ZoomTarget {
  style: { zoom?: string };
}

function parseZoom(raw: string | undefined): number {
  const n = parseFloat(String(raw || ''));
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export function applyZoomCompensation(
  win: ZoomWin,
  wrap: ZoomTarget | null,
): void {
  if (!wrap) return;
  const htmlZoom = parseZoom(
    win.getComputedStyle(win.document.documentElement).zoom,
  );
  const bodyZoom = parseZoom(win.getComputedStyle(win.document.body).zoom);
  const total = htmlZoom * bodyZoom;
  if (total !== 1) {
    wrap.style.zoom = String(1 / total);
  } else {
    wrap.style.zoom = '';
  }
}

export type SetTimeoutFn = (fn: () => void, ms: number) => unknown;

export function scheduleZoomRetries(
  win: ZoomWin,
  wrap: ZoomTarget | null,
  setTimeoutFn: SetTimeoutFn,
  delays: readonly number[] = DEFAULT_ZOOM_RETRY_DELAYS,
): void {
  for (const delay of delays) {
    setTimeoutFn(() => applyZoomCompensation(win, wrap), delay);
  }
}
